/**
 * ItemCard - Generic item card component used by all flavors.
 * Renders in either "card" (horizontal scroll) or "compact" (list) mode.
 *
 * Required options:
 * - product: { id, name, brand, price, currency, unit, imageUrl, inStock, category, sellBy, cartQuantity }
 * - compact: boolean (list mode vs card mode)
 * - onAdd: (cardEl, product) => void
 *
 * Optional:
 * - hasVariants: (product) => boolean — external variant detection
 * - onVariantPick: (cardEl, product) => void — called instead of normal add for variant items
 *
 * sellBy modes:
 *   "quantity" (default) — integer stepper (step 1, min 1)
 *   "weight"             — weight stepper (step 0.5, min 0.5, displays "kg")
 *
 * cartQuantity / cart_quantity:
 *   If provided and > 0, the stepper starts at that value and the button reads "Update".
 */
export class ItemCard {
  constructor(product, options = {}) {
    this.product = product;
    this.compact = options.compact || false;
    this.primaryColor = options.primaryColor || '#7c3aed';
    this.onAdd = options.onAdd || (() => {});
    this.onAddDisabled = options.onAddDisabled || (() => false);
    this.onPickerOpen = options.onPickerOpen || (() => {});
    this.onPickerClose = options.onPickerClose || (() => {});
    this.hasVariants = options.hasVariants || (() => false);
    this.onVariantPick = options.onVariantPick || null;
    this.element = null;

    const sellBy = product.sellBy || product.sell_by || 'quantity';
    this.sellBy = sellBy;
    this.step = sellBy === 'weight' ? 0.5 : 1;
    this.min = sellBy === 'weight' ? 0.5 : 1;
    this.unit = sellBy === 'weight' ? 'kg' : '';

    const cartQty = product.cartQuantity || product.cart_quantity || 0;
    this.inCart = cartQty > 0;
    this.amount = this.inCart ? cartQty : this.min;
  }

  _generateStepperHTML() {
    const displayValue = this.sellBy === 'weight'
      ? `${this.amount.toFixed(1)} ${this.unit}`
      : `${this.amount}`;

    return `
      <div class="ttp-stepper" data-sell-by="${this.sellBy}">
        <button class="ttp-stepper-btn ttp-stepper-minus" type="button">\u2212</button>
        <span class="ttp-stepper-value">${displayValue}</span>
        <button class="ttp-stepper-btn ttp-stepper-plus" type="button">+</button>
      </div>
    `;
  }

  _updateStepperDisplay(valueDisplay, minusBtn) {
    if (this.sellBy === 'weight') {
      valueDisplay.textContent = `${this.amount.toFixed(1)} ${this.unit}`;
    } else {
      valueDisplay.textContent = `${this.amount}`;
    }
    minusBtn.disabled = this.amount <= this.min;
    minusBtn.classList.toggle('ttp-stepper-disabled', this.amount <= this.min);
  }

  render() {
    const el = document.createElement('div');
    el.className = this.compact ? 'ttp-product-compact' : 'ttp-product-card';

    const p = this.product;
    const currencySymbol = p.currency === 'ILS' ? '\u20AA' : '$';

    const needsPicker = this.hasVariants(p);
    const btnLabel = needsPicker ? 'Choose' : (this.inCart ? 'Update' : 'Add');
    const btnClass = needsPicker
      ? 'ttp-add-btn ttp-choose-btn'
      : (this.inCart ? 'ttp-add-btn ttp-update-btn' : 'ttp-add-btn');

    if (this.compact) {
      el.innerHTML = `
        <div class="ttp-product-image-sm">
          <img src="${p.imageUrl}" alt="${p.name}" onerror="this.style.display='none'" />
        </div>
        <div class="ttp-product-info">
          <div class="ttp-product-name">
            ${p.name}
            ${p.productPageUrl ? `<a class="ttp-product-page-link" href="${p.productPageUrl}" onclick="event.stopPropagation()">↗</a>` : ''}
          </div>
          <div class="ttp-product-meta">${p.unit} \u00B7 ${p.brand}</div>
        </div>
        <div class="ttp-product-action">
          <div class="ttp-product-price">${currencySymbol}${p.price.toFixed(2)}</div>
          ${p.inStock
            ? `<button class="${btnClass}">${btnLabel}</button>`
            : '<span class="ttp-out-of-stock">Out of stock</span>'}
          ${this._generateStepperHTML()}
        </div>
      `;
    } else {
      el.innerHTML = `
        <div class="ttp-product-image">
          <img src="${p.imageUrl}" alt="${p.name}" onerror="this.style.display='none'" />
          ${!p.inStock ? '<div class="ttp-stock-badge">Out of stock</div>' : ''}
        </div>
        <div class="ttp-product-details">
          <div class="ttp-product-name">
            ${p.name}
            ${p.productPageUrl ? `<a class="ttp-product-page-link" href="${p.productPageUrl}" onclick="event.stopPropagation()">↗</a>` : ''}
          </div>
          <div class="ttp-product-meta">${p.unit}</div>
          <div class="ttp-product-footer">
            <span class="ttp-product-price">${currencySymbol}${p.price.toFixed(2)}</span>
            ${p.inStock
              ? `<button class="${btnClass}">${btnLabel}</button>`
              : '<span class="ttp-out-of-stock">Out of stock</span>'}
            ${this._generateStepperHTML()}
          </div>
        </div>
      `;
    }

    const addBtn = el.querySelector('.ttp-add-btn');
    if (addBtn) {
      addBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.onAddDisabled()) return;

        if (needsPicker && this.onVariantPick) {
          this.onVariantPick(el, this.product);
          return;
        }

        addBtn.textContent = '\u2713';
        addBtn.classList.add('ttp-added');
        addBtn.disabled = true;

        const productData = {
          ...this.product,
          selectedAmount: this.amount,
          sellBy: this.sellBy
        };

        this.onAdd(el, productData);
        this.inCart = true;

        setTimeout(() => {
          addBtn.textContent = 'Update';
          addBtn.classList.remove('ttp-added');
          addBtn.classList.add('ttp-update-btn');
          addBtn.disabled = false;
        }, 1500);
      });
    }

    // Stepper handlers
    const minusBtn = el.querySelector('.ttp-stepper-minus');
    const plusBtn = el.querySelector('.ttp-stepper-plus');
    const valueDisplay = el.querySelector('.ttp-stepper-value');

    if (minusBtn && plusBtn && valueDisplay) {
      minusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.amount > this.min) {
          this.amount = Math.round((this.amount - this.step) * 10) / 10;
          this._updateStepperDisplay(valueDisplay, minusBtn);
        }
      });

      plusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.amount = Math.round((this.amount + this.step) * 10) / 10;
        this._updateStepperDisplay(valueDisplay, minusBtn);
      });

      this._updateStepperDisplay(valueDisplay, minusBtn);
    }

    this.element = el;
    return el;
  }
}
